import {Injectable, inject} from '@angular/core'
import type {OfferPKP} from '../../entities/modals/offer-modals/offer-pkp.modal'
import type {ProductInfo} from '../../entities/modals/product-modals/product.model'
import type {ContactPerson} from '../../entities/modals/request-modals/contact-person.modal'
import {type Observable, map} from 'rxjs'
import {environment} from '@cart/src/environments/environment'
import {OrganizationParametersService} from '@services/organization-parameters.service'
import {ConversationStatus} from '@uve-solutions/f-chat/app'
import {ChatContract} from '@uve-solutions/f-chat/core'
import {
  ChatConfig,
  ChatEnvironment,
  type ChatOrigin,
  ChatOriginType,
  Platform as ChatPlatform,
  type Product as ChatProduct,
  type Conversation,
  type TemplateMessage,
  type User as UserSimplified,
} from '@uve-solutions/f-chat/models'
import {SortOptionsEnum} from '../../entities/enums/sort-options.enum'
import {
  ApiProductFiltersRequest,
  ApiProductFiltersUveCartRequest,
  ApiProductRequestModal,
} from '../../entities/modals/request-modals/api-product-request.modal'
import {CatalogService} from '../../services/catalog.service'
import {ChatListService} from '../../services/chat-list.service'
import {ConversationService} from '../../services/conversation.service'
import {CurrencyService} from '../../services/currency.service'
import {InfoService} from '../../services/info.service'
import {OutletService} from '../../services/outlet.service'
import {ProductCompatibilityService} from '../../services/product-compatibility.service'
import {TranslationServiceX} from '../../services/translationx.service'
import {UserService} from '../../services/user.service'
import {ChatApiAdapter} from './chat-api.adapter'

@Injectable({providedIn: 'root'})
export class ChatFacade extends ChatContract {
  environment: ChatEnvironment = {
    emailIntegratorBaseUrl: environment.emailIntegratorUrl,
  }

  apiService = inject(ChatApiAdapter)
  chatConfig: ChatConfig = new ChatConfig()
  reactivity = {socketIoUrl: environment.chatAppUrl}

  readonly #translationService = inject(TranslationServiceX)
  readonly #conversationService = inject(ConversationService)
  readonly #chatListService = inject(ChatListService)
  readonly #userService = inject(UserService)
  readonly #infoService = inject(InfoService)
  readonly #outletService = inject(OutletService)
  readonly #currencyService = inject(CurrencyService)
  readonly #catalogService = inject(CatalogService)
  readonly #organizationParametersService = inject(OrganizationParametersService)

  constructor() {
    super()

    this.chatConfig = {
      language: this.#translationService.getCurrentLanguage(),
      platform: this.getCurrentPlatform(),
    }
  }

  translate(value: string, replacements?: {[key: string]: string} | null): string {
    if (!value) {
      return ''
    }
    return this.#translationService.instant(value, replacements)
  }

  transformCurrency(value: number): string {
    return this.#currencyService.formatPrice(value)
  }

  setConversationStatus(origin: ChatOrigin | null = null): void {
    const conversation = this.getConversation()
    const chatUser = this.#conversationService.getChatUser()
    this.#setChatStatus(chatUser, origin)
    if (conversation) {
      this.#chatListService.getContactList(false, {channelId: conversation.channelId}).subscribe(res => {
        const conversationResponse = res?.find(r => r.channelId === conversation.channelId)
        if (conversationResponse) {
          this.#setChatStatus(chatUser, origin, conversationResponse.chatStatus)
        }
      })
    }
  }

  #setChatStatus(chatUser: ContactPerson, origin: ChatOrigin | null, status: ConversationStatus = null): void {
    const isTemplateMessage = origin?.type === ChatOriginType.Ecommerce && !!origin?.template
    const isMessageFromWhatsapp = origin?.type === ChatOriginType.WhatsApp

    let newStatus = status ?? chatUser.chatStatus

    if (isTemplateMessage && newStatus === ConversationStatus.Closed) {
      newStatus = ConversationStatus.Waiting
    }

    if (isMessageFromWhatsapp) {
      newStatus = ConversationStatus.Open
    }

    chatUser.chatStatus = newStatus
    this.#conversationService.setChatUser(chatUser)
  }

  getCurrentUser(): UserSimplified {
    const user = this.#userService.getUser()
    if (!user) {
      return null
    }

    return {
      organizationId: user.organizationId,
      id: user.userId as unknown as number,
      userName: user.userName,
      mobileNumber: user.mobileNumber,
      countryCode: user.countryCode,
      type: user.type,
      userAccessType: user.userAccessType,
      isImpersonate: user.isImpersonate,
      originalUser: user.originalUser,
    } as UserSimplified
  }

  getConversation(): Conversation {
    const chatUser = this.#conversationService.getChatUser()
    if (!chatUser) {
      return null
    }

    const outlet = this.#outletService.getSelectedOutlet()
    return {
      channelId: chatUser.channelId,
      country_code: chatUser.country_code,
      userId: chatUser.userId,
      mobile_no: chatUser.mobile_no,
      firstname: chatUser.firstname,
      customerId: outlet?.customerId,
      customerCode: outlet?.customerCode,
    }
  }

  showPrices(categoryId: string, subCategoryId: string): boolean {
    return this.#outletService.showPrices(categoryId, subCategoryId)
  }

  getCatalogProducts(searchTerm: string, limit: number, offset: number): Observable<ChatProduct[]> {
    const productFilterRequest: ApiProductFiltersRequest = new ApiProductFiltersRequest()
    productFilterRequest.name = searchTerm
    productFilterRequest.uvecart = new ApiProductFiltersUveCartRequest()

    const filters: ApiProductRequestModal = new ApiProductRequestModal()
    filters.filters = productFilterRequest
    filters.limit = limit
    filters.offset = offset
    filters.returnOnlyPaginationProducts = true
    filters.order = SortOptionsEnum.a_to_z

    return this.#catalogService.getProductsWithCustomFilter(filters, false).pipe(
      map((res: {offers: OfferPKP[]; products: ProductInfo[]}) => {
        if (res?.products?.length) {
          return res.products.map(
            p => ProductCompatibilityService.castProductInfoToProduct(p) as unknown as ChatProduct
          )
        } else {
          return []
        }
      })
    )
  }
  getParamTemplates(): TemplateMessage[] {
    return this.#organizationParametersService.getChatTemplates()
  }

  private getCurrentPlatform(): ChatPlatform {
    return (
      this.#infoService.isAndroid() ? ChatPlatform.Android
      : this.#infoService.isIos() ? ChatPlatform.iOS
      : ChatPlatform.Web
    )
  }
}
